import { reflect } from '@effector/reflect';
import { NumberInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { Coins } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Cashdesk } from 'shared/api';
import { translated, $t } from 'shared/lib';
import { createModalForm, ModalFormButtonProps, ModalFormProps } from 'shared/ui';
import { DepositCashdeskModel } from '../../model';

export const createDepositCashdeskModal = (model: DepositCashdeskModel) => {
	const ModalForm = createModalForm(model);

	const AmountField = reflect({
		view: NumberInput,
		bind: {
			'data-test-id': 'deposit-cashdesk-amount-input',
			value: model.form.fields.amount.$value,
			onChange: (e) => model.form.fields.amount.change(`${e}`),
			label: translated('deposit-cashdesk.amount'),
			placeholder: '250.00',
			rightSection: model.$modalData.map((cashdesk) => cashdesk?.currency ?? null),
			errorProps: { 'data-test-id': 'deposit-cashdesk-amount-input-error' },
			error: combine(
				[model.form.fields.amount.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('deposit-cashdesk.amount'),
					}),
			),
			required: true,
		},
	});

	const DepositCashdeskModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const pending = useUnit(model.$pending);

		return (
			<ModalForm
				ref={ref}
				title={t('deposit-cashdesk.title')}
				submitButtonProps={{
					children: t('deposit-cashdesk.deposit'),
					loading: pending,
				}}
				cancelButtonProps={{
					children: t('deposit-cashdesk.cancel'),
				}}
				{...props}
			>
				<AmountField />
			</ModalForm>
		);
	});

	const DepositCashdeskModalButton = forwardRef<HTMLButtonElement, ModalFormButtonProps<Cashdesk>>(
		({ modalData, ...props }, ref) => {
			const { t } = useTranslation();

			return (
				<ModalForm.Button
					ref={ref}
					data-test-id='deposit-cashdesk-button'
					leftSection={<Coins />}
					disabled={modalData.isLocked}
					modalData={modalData}
					{...props}
				>
					{t('deposit-cashdesk.deposit')}
				</ModalForm.Button>
			);
		},
	);

	return Object.assign(DepositCashdeskModal, {
		Button: DepositCashdeskModalButton,
	});
};
